import type { Language } from '@offer-catalog/services/i18n'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'
import type { CustomViewDataPoint, CustomViewSelections } from '@/contexts/products/customViewConfig'
import type { GallerySM } from '@/types/catalog/gallery'
import { VpDivider, VpPrice, VpPriceAmount } from '@vtmn-play/react/headless'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomerReviews } from '@/components/ProductCard/Parts/CustomerReviews'
import { DataFallback } from '@/components/ProductCard/Parts/DataFallback'
import { DistributionCustomsZones } from '@/components/ProductCard/Parts/DistributionCustomsZones'
import { ProductAvailability } from '@/components/ProductCard/Parts/ProductAvailabillity'
import { ProductBenefits } from '@/components/ProductCard/Parts/ProductBenefits'
import { ProductBrand } from '@/components/ProductCard/Parts/ProductBrand'
import { ProductComposition } from '@/components/ProductCard/Parts/ProductComposition'
import { ProductDesign } from '@/components/ProductCard/Parts/ProductDesign'
import { ProductNature } from '@/components/ProductCard/Parts/ProductNature'
import { useCzListSetsContext } from '@/contexts/cz-list-sets/useCzListSetsContext'

type ProductsListInfoGroup
  = | 'main-information'
    | 'product-information'
    | 'benefits'
    | 'internet-teaser'
    | 'designed-in'
    | 'distribution-customs-zones'
    | 'availability-dates'

interface ProductsListInfoItem {
  key: string
  group: ProductsListInfoGroup
  content: ReactNode
}

interface ProductsListInfoVisibility {
  dmiSegmentation: boolean
  price: boolean
  customerReview: boolean
  brand: boolean
  gender: boolean
  productNature: boolean
  composition: boolean
  productBenefits: boolean
  internetTeaser: boolean
  designedIn: boolean
  distributionCustomsZones: boolean
  availabilityDates: boolean
}

function getInfoVisibility(customView: CustomViewSelections | undefined, product: GallerySM): ProductsListInfoVisibility {
  const isSelected = (key: CustomViewDataPoint) => customView?.selectedItems.includes(key) ?? true

  return {
    dmiSegmentation: isSelected('dmiSegmentation'),
    price: isSelected('price'),
    customerReview: isSelected('customerReview'),
    brand: isSelected('brand'),
    gender: isSelected('gender'),
    productNature: isSelected('productNature'),
    composition: isSelected('composition'),
    productBenefits: isSelected('productBenefits') && Boolean(product.product_benefits?.[0]),
    internetTeaser: isSelected('internetTeaser'),
    designedIn: isSelected('designedIn'),
    distributionCustomsZones: isSelected('distributionCustomsZones'),
    availabilityDates: isSelected('availabilityDates'),
  }
}

function createInfoItems(
  product: GallerySM,
  visibility: ProductsListInfoVisibility,
  currentLanguage: Language,
  t: TFunction,
  countryCode: string | undefined,
  designCenterDesignation: string | undefined,
): ProductsListInfoItem[] {
  const {
    availability_date,
    brand,
    composition,
    design_center,
    dmi_segmentation,
    genders,
    price,
    product_benefits,
    product_nature,
    review,
    super_model,
    selected_customs_zone,
    web_catch_line,
  } = product
  const items: (ProductsListInfoItem | false | null | undefined)[] = [
    visibility.dmiSegmentation && {
      key: 'technicity-level',
      group: 'main-information',
      content: (
        <div className="products-list-row__segmentation" data-testid={`segmentation_${super_model.id}`}>
          <DataFallback
            value={dmi_segmentation?.technicity_level}
            fallbackKey="products-page.product-card.no-segmentation"
          >
            {technicityLevel => technicityLevel.replace('_', ' ')}
          </DataFallback>
        </div>
      ),
    },
    {
      key: 'identity',
      group: 'main-information',
      content: (
        <>
          <div className="products-list-row__sm-code" data-testid={`sm-code_${super_model.id}`}>
            <span className="sr-only">{super_model.id.toString().split('').join(' ')}</span>
            <span aria-hidden="true">{super_model.id}</span>
          </div>
          <div className="products-list-row__sm-name" data-testid={`sm-name_${super_model.id}`}>{super_model.labels[currentLanguage]}</div>
        </>
      ),
    },
    visibility.price && {
      key: 'price',
      group: 'main-information',
      content: (
        <div className="basic-info-price">
          <DataFallback
            value={price}
            fallbackKey="products-page.product-card.no-price"
            isEmpty={value => value.amount == null || !value.currency}
          >
            {value => (
              <VpPrice>
                <VpPriceAmount>
                  {value.amount}
                  {' '}
                  {value.currency}
                </VpPriceAmount>
              </VpPrice>
            )}
          </DataFallback>
        </div>
      ),
    },
    visibility.customerReview && review && {
      key: 'customer-review',
      group: 'main-information',
      content: <CustomerReviews review={review} />,
    },
    visibility.brand && {
      key: 'brand',
      group: 'product-information',
      content: <ProductBrand id={brand?.id.toString()} />,
    },
    visibility.gender && {
      key: 'gender',
      group: 'product-information',
      content: (
        <div className="products-list-row__gender" data-testid={`gender_${super_model.id}`}>
          <span className="sr-only">
            {t('products-page.filters.groups.genders')}
            :
            {' '}
          </span>
          <DataFallback
            value={genders}
            fallbackKey="products-page.product-card.no-gender"
            isEmpty={items => items.length === 0}
          >
            {items => t(`products-page.product-card.gender-id.${items[0].id}`)}
          </DataFallback>
        </div>
      ),
    },
    visibility.productNature && {
      key: 'product-nature',
      group: 'product-information',
      content: <ProductNature id={product_nature?.id} />,
    },
    visibility.composition && {
      key: 'composition',
      group: 'product-information',
      content: <ProductComposition composition={composition} />,
    },
    visibility.productBenefits && {
      key: 'benefits',
      group: visibility.brand || visibility.productNature || visibility.composition ? 'benefits' : 'product-information',
      content: <ProductBenefits benefits={product_benefits ?? []} />,
    },
    visibility.internetTeaser && {
      key: 'internet-teaser',
      group: 'internet-teaser',
      content: (
        <DataFallback
          value={web_catch_line}
          fallbackKey="products-page.product-card.no-teaser"
        >
          {teaser => <div className="products-list-row__internet-teaser">{teaser}</div>}
        </DataFallback>
      ),
    },
    visibility.designedIn && {
      key: 'designed-in',
      group: 'designed-in',
      content: (
        <ProductDesign
          designCenter={design_center}
          countryCode={countryCode}
          designation={designCenterDesignation}
          className="products-list-row__design-center"
          labelClassName="products-list-row__design-center-label"
          designationClassName="products-list-row__cz-label"
        />
      ),
    },
    visibility.distributionCustomsZones && {
      key: 'distribution-customs-zones',
      group: 'distribution-customs-zones',
      content: <DistributionCustomsZones selectedCustomsZones={selected_customs_zone} />,
    },
    visibility.availabilityDates && {
      key: 'availability-dates',
      group: 'availability-dates',
      content: <ProductAvailability availabilityDate={availability_date} />,
    },
  ]

  return items.filter((item): item is ProductsListInfoItem => Boolean(item))
}

function ProductsListInfoItems({ items }: Readonly<{ items: ProductsListInfoItem[] }>) {
  return (
    <>
      {items.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && item.group !== items[index - 1].group && <VpDivider />}
          {item.content}
        </Fragment>
      ))}
    </>
  )
}

export function ProductsListRowInfo({ product, customView }: Readonly<{ product: GallerySM, customView?: CustomViewSelections }>) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const { czListSetsByCustomZone } = useCzListSetsContext()
  const designCenter = product.design_center
  const designCenterDetails = designCenter?.id
    ? czListSetsByCustomZone[designCenter.id]
    : undefined
  const countryCode = designCenterDetails?.sales_organization?.substring(0, 2).toUpperCase()
  const visibility = getInfoVisibility(customView, product)
  const infoItems = createInfoItems(
    product,
    visibility,
    currentLanguage,
    t,
    countryCode,
    designCenterDetails?.designation,
  )

  return (
    <div className="products-list-row__sm-info">
      <ProductsListInfoItems items={infoItems} />
    </div>
  )
}
